<template>
  <v-container v-if="item" :key="'wrapper-' + configKey" fluid>
    <v-row v-if="item.Enable || item.From" no-gutters class="mb-3" align="start">
      <v-col v-if="item.Enabled" cols="auto" class="d-flex align-center pt-1">
        <v-chip small outlined style="border-radius: 4px;" :color="displayEnabledBool ? 'primary' : ''">
          {{ displayEnabledBool ? $t('servicesystem.common_objects.commonterms.enabled') :
            $t('servicesystem.common_objects.commonterms.deactivated') }}
        </v-chip>
        <v-switch v-if="showEnabledSwitch" v-model="enabledBool" dense hide-details class="mt-0 ml-3"
          @focus="handleFocus && handleFocus(true)" @blur="handleFocus && handleFocus(false)">
        </v-switch>
      </v-col>

      <v-col v-if="item.From" cols="0">
        <QTextField v-model="item.From.Value" :externaledit="editMode" singleline :rules="[emailRule]"
          :class="item.Enabled ? 'ml-4' : ''" @FocusState="handleFocus">
          <template v-slot:prepend-outer>
            <v-chip small outlined style="border-radius: 4px;">
              {{ $t('servicesystem.common_objects.email_settings.from') }}
            </v-chip>
          </template>
          <template v-if="presenceSource === 'From'" v-slot:append-outer>
            <QInfo :text="presenceFromHint" icon="mdi-link-variant" color="primary" dense style="margin-top: 9px;">
            </QInfo>
          </template>
        </QTextField>
      </v-col>
    </v-row>

    <v-row v-if="item.To" no-gutters class="mb-3" align="end">
      <v-col>
        <QTextField v-model="item.To.Value" :externaledit="editMode" placeholder="someone@corp.com" :rules="[emailRule]"
          singleline @FocusState="handleFocus">
          <template v-slot:prepend-outer>
            <v-chip small outlined style="border-radius: 4px;">
              {{ $t('servicesystem.common_objects.email_settings.to') }}
            </v-chip>
          </template>

          <template v-if="presenceSource === 'To'" v-slot:append-outer>
            <QInfo :text="presenceToHint" icon="mdi-link-variant" color="primary" dense />
          </template>
        </QTextField>
      </v-col>
    </v-row>

    <v-row v-if="item.CC" no-gutters class="mb-3" align="end">
      <v-col>
        <QTextField v-model="item.CC.Value" :externaledit="editMode" singleline :rules="[emailRule]"
          @FocusState="handleFocus">
          <template v-slot:prepend-outer>
            <v-chip small outlined style="border-radius: 4px;">
              {{ $t('servicesystem.common_objects.email_settings.cc') }}
            </v-chip>
          </template>
        </QTextField>
      </v-col>
    </v-row>

    <v-row v-if="item.Subject" no-gutters class="mb-3">
      <v-col>
        <QTextField class="email-subject" v-model="item.Subject.Value"
          :label="$t('servicesystem.common_objects.email_settings.header')"
          :placeholder="$t('servicesystem.common_objects.email_settings.header')" :externaledit="editMode" singleline
          @FocusState="handleFocus">
        </QTextField>
      </v-col>
    </v-row>

    <v-row v-if="item.Body" no-gutters>
      <v-col>
        <QTextArea v-model="item.Body.Value" :label="$t('servicesystem.common_objects.email_settings.body')"
          class="emailbodyfield" :externaledit="editMode" @FocusState="handleFocus">
        </QTextArea>
      </v-col>
    </v-row>
  </v-container>
</template>


<script>
export default {
  name: "EmailEditContent",
  props: {
    configKey: String,
    item: Object, // EmailSettingTemplateDto
    editMode: Boolean,
    handleFocus: Function,
    replaceKeyWords: Array,
  },
  components: {},
  mixins: [],

  data() {
    return {
    };
  },

  mounted() {
    //console.log('item', this.item);
  },

  methods: {
    isLikelyEmail(value) {
      if (!value) return false;
      const v = String(value).trim();
      // simple heuristic: must contain one "@" and a dot after it
      const at = v.indexOf("@");
      const dot = v.lastIndexOf(".");
      return at > 0 && dot > at + 1 && dot < v.length - 1;
    },
  },

  computed: {
    showEnabledSwitch() {
      const enabledName = this.item?.Enabled?.SettingName;
      if (!enabledName) {
        return false;
      }
      const fromName = this.item?.From?.SettingName;
      const toName = this.item?.To?.SettingName;
      const isPresenceBased = enabledName === fromName || enabledName === toName;
      return !isPresenceBased;
    },

    // Reflects effective "enabled" considering presence-based logic
    displayEnabledBool() {
      if (this.isPresenceBased) {
        // Enabled presence depends on which field it mirrors
        const enabledName = this.item?.Enabled?.SettingName;
        const fromName = this.item?.From?.SettingName;
        const toName = this.item?.To?.SettingName;
        if (enabledName && enabledName === fromName) {
          const val = (this.item?.From?.Value || "").trim();
          return this.isLikelyEmail(val);
        }
        if (enabledName && enabledName === toName) {
          const val = (this.item?.To?.Value || "").trim();
          return !!val;
        }
        return false;
      }
      return this.enabledBool;
    },

    isPresenceBased() {
      const enabledName = this.item?.Enabled?.SettingName;
      const fromName = this.item?.From?.SettingName;
      const toName = this.item?.To?.SettingName;
      return !!enabledName && (enabledName === fromName || enabledName === toName);
    },

    presenceSource() {
      const enabledName = this.item?.Enabled?.SettingName;
      if (!enabledName) return null;
      const fromName = this.item?.From?.SettingName;
      const toName = this.item?.To?.SettingName;
      if (enabledName === fromName) return 'From';
      if (enabledName === toName) return 'To';
      return null;
    },

    presenceRuleText() {
      if (this.presenceSource === 'From') {
        return 'Enabled when "From" contains a valid email address.';
      }
      if (this.presenceSource === 'To') {
        return 'Enabled when "To" is populated.';
      }
      return '';
    },

    presenceFromHint() {
      return 'Filling this field enables the template.';
    },
    presenceToHint() {
      return 'Filling this field enables the template.';
    },

    enabledBool: {
      get() {
        const val = this.item?.Enabled?.Value;
        return String(val).toLowerCase() === 'true';
      },
      set(v) {
        if (this.item && this.item.Enabled) {
          this.item.Enabled.Value = v ? 'true' : 'false';
        }
      }
    },
  },

  watch: {},
};
</script>


<style lang='scss'>
.emailbodyfield {
  textarea {
    max-height: 450px;
    overflow-y: auto;
    font-size: 12px;
    line-height: 1.3;
    /* optional tighter padding for more visible lines */
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
}

/* Make subject input look more like a header */
.email-subject input {
  font-weight: 600;
  font-size: 13px;
}
</style>